import React, { useId, useState } from 'react';
import clsx from 'clsx';
import { chipContentPadding, chipTextClass, useFilterBarDensity } from './filterBarDensity';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import CustomTooltip from '@components/ringil3/Tooltip/CustomTooltip.tsx';
import SvgSortAsc from '@icons/sort-ascending-filled.svg?react';
import SvgSortDesc from '@icons/sort-descending-filled.svg?react';
import SvgChevron from '@icons/chevron.svg?react';
import { FilterSort } from '@hooks/useFilter/Filter.tsx';
import { SortableColumn } from '../types';

interface Props {
  sort?: FilterSort;
  applySorting?: (sortField: string, sortDirection: 'ASC' | 'DESC') => void;
  sortableColumns?: SortableColumn[];
}

export const SortChip: React.FC<Props> = ({ sort, applySorting, sortableColumns }) => {
  const { tr } = useTranslationLgs();
  const [open, setOpen] = useState(false);
  const directionToggleId = `sort-direction-${useId().replace(/:/g, '-')}`;

  // Read before the early return: it is a hook.
  const density = useFilterBarDensity();
  if (!applySorting || !sortableColumns || sortableColumns.length === 0) return null;

  const direction = sort?.sortDirection ?? 'DESC';
  const currentField = sort?.sortField ?? sortableColumns[0].field;
  const orderLabel = tr('RingilDataTable.order', 'Order');
  const byLabel = tr('RingilDataTable.by', 'by');
  // Tooltip describes what clicking does, not the current state: ASC now → click sorts descending.
  const directionActionLabel = direction === 'ASC' ? tr('RingilDataTable.sortDescAction', 'Sort descending') : tr('RingilDataTable.sortAscAction', 'Sort ascending');

  const toggleDirection = () => applySorting(currentField, direction === 'ASC' ? 'DESC' : 'ASC');

  return (
    <div
      className={clsx(
        "inline-flex items-center rounded border border-solid border-content-brand bg-content-brandMedium font-['DM_Sans'] font-normal text-content-primary",
        chipTextClass(density),
      )}
    >
      <button
        type="button"
        id={directionToggleId}
        onClick={toggleDirection}
        className={clsx('inline-flex items-center bg-transparent border-none cursor-pointer', chipContentPadding(density))}
        data-testid="sort-direction-toggle"
      >
        {direction === 'ASC' ? <SvgSortAsc className="w-3.5 h-3.5 text-content-brand" /> : <SvgSortDesc className="w-3.5 h-3.5 text-content-brand" />}
      </button>
      <CustomTooltip targetSelector={`#${directionToggleId}`} content={directionActionLabel} />
      <span className="w-px self-stretch bg-content-brand" />
      <div className="relative inline-flex items-center">
        <Dropdown
          value={currentField}
          options={sortableColumns}
          optionLabel="label"
          optionValue="field"
          onChange={e => e.value && applySorting(e.value, direction)}
          onShow={() => setOpen(true)}
          onHide={() => setOpen(false)}
          valueTemplate={(option: SortableColumn | undefined) => (
            <span>
              <span className="font-normal text-content-primary">{orderLabel} </span>
              <span className="font-bold text-content-primary">
                {byLabel} {option?.label ?? sortableColumns.find(c => c.field === currentField)?.label}
              </span>
            </span>
          )}
          data-testid="sort-field-dropdown"
          className={clsx(
            '!w-auto !min-h-0 !h-auto !p-0 !m-0 !border-0 !bg-transparent !shadow-none !outline-none',
            '[&.p-focus]:!border-0 [&.p-focus]:!shadow-none [&.p-inputwrapper-filled]:!border-0',
            '[&_.p-dropdown-label]:!min-h-0 [&_.p-dropdown-label]:!h-auto [&_.p-dropdown-label]:!m-0 [&_.p-dropdown-label]:!border-0 [&_.p-dropdown-label]:!bg-transparent [&_.p-dropdown-label]:!pl-2 [&_.p-dropdown-label]:!pr-7 [&_.p-dropdown-label]:!text-content-primary',
            density === 'compact'
              ? '[&_.p-dropdown-label]:!py-0 [&_.p-dropdown-label]:!leading-4 [&_.p-dropdown-label]:!text-xs'
              : '[&_.p-dropdown-label]:!py-1 [&_.p-dropdown-label]:!leading-6',
            '[&_.p-dropdown-trigger]:!hidden',
          )}
          panelClassName="text-base !mt-1"
        />
        <SvgChevron
          className={clsx('pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-content-brand transition-transform', open && 'rotate-180')}
        />
      </div>
    </div>
  );
};
